import {
  ENTITLEMENTS,
  type ConnectionIntent,
  type PairDimensionId,
  type PairScoreDoc,
} from '@astrally/shared';
import type { MatchableProfile } from './profile.js';
import { pairIdOf } from './profile.js';
import { toDimensionScore } from './grades.js';
import {
  communicationFit,
  geoPracticality,
  intentionFit,
  jyotishCompat,
  lifeStageFit,
  reliabilityFit,
  sharedInterests,
  westernCompat,
} from './dimensions.js';

/**
 * Intent-dependent weights. Romance leans on life-stage and astrology;
 * professional intent leans on interests and communication. Weight sets are
 * averaged across the pair's shared intents.
 */
const WEIGHTS: Record<string, Record<PairDimensionId, number>> = {
  romance: {
    intentionFit: 0.14,
    communicationFit: 0.16,
    sharedInterests: 0.14,
    lifeStageFit: 0.16,
    geoPracticality: 0.1,
    jyotishCompat: 0.14,
    westernCompat: 0.09,
    reliability: 0.07,
  },
  professional: {
    intentionFit: 0.2,
    communicationFit: 0.2,
    sharedInterests: 0.25,
    lifeStageFit: 0.08,
    geoPracticality: 0.05,
    jyotishCompat: 0.05,
    westernCompat: 0.05,
    reliability: 0.12,
  },
  default: {
    intentionFit: 0.16,
    communicationFit: 0.17,
    sharedInterests: 0.18,
    lifeStageFit: 0.12,
    geoPracticality: 0.09,
    jyotishCompat: 0.1,
    westernCompat: 0.08,
    reliability: 0.1,
  },
};

function weightsFor(intents: ConnectionIntent[]): Record<PairDimensionId, number> {
  const sets = intents.map((i) => {
    if (i === 'romance') return WEIGHTS.romance;
    if (i === 'professional' || i === 'founder' || i === 'mentorship') return WEIGHTS.professional;
    return WEIGHTS.default;
  });
  if (sets.length === 0) return WEIGHTS.default;
  const keys = Object.keys(WEIGHTS.default) as PairDimensionId[];
  const out = {} as Record<PairDimensionId, number>;
  for (const k of keys) {
    out[k] = sets.reduce((s, w) => s + w[k], 0) / sets.length;
  }
  return out;
}

export interface PairScoreExtras {
  sharedInterestList: string[];
  sharedIntents: ConnectionIntent[];
}

export function computePairScore(
  a: MatchableProfile,
  b: MatchableProfile,
  now: Date = new Date(),
): PairScoreDoc & { extras: PairScoreExtras } {
  const sharedIntents = a.intents.filter((i) => b.intents.includes(i));
  const interests = sharedInterests(a, b);

  const dims: Record<PairDimensionId, number> = {
    intentionFit: intentionFit(a, b),
    communicationFit: communicationFit(a, b),
    sharedInterests: interests.score,
    lifeStageFit: lifeStageFit(a, b),
    geoPracticality: geoPracticality(a, b),
    jyotishCompat: jyotishCompat(a.astro, b.astro),
    westernCompat: westernCompat(a.astro, b.astro, sharedIntents),
    reliability: reliabilityFit(a, b),
  };

  const weights = weightsFor(sharedIntents);
  const overallScore = (Object.keys(dims) as PairDimensionId[]).reduce(
    (sum, k) => sum + dims[k] * weights[k],
    0,
  );

  const dimensions = Object.fromEntries(
    (Object.keys(dims) as PairDimensionId[]).map((k) => [k, toDimensionScore(dims[k])]),
  ) as PairScoreDoc['dimensions'];

  return {
    pairId: pairIdOf(a.uid, b.uid),
    aUid: [a.uid, b.uid].sort()[0],
    bUid: [a.uid, b.uid].sort()[1],
    scoringVersion: ENTITLEMENTS.scoringVersion,
    dimensions,
    overall: toDimensionScore(overallScore),
    scoredIntents: sharedIntents,
    computedAtISO: now.toISOString(),
    extras: {
      sharedInterestList: interests.shared,
      sharedIntents,
    },
  };
}
